import { NgTemplateOutlet } from '@angular/common';
import {
	afterNextRender,
	Component,
	contentChild,
	DOCUMENT,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	Renderer2,
	signal,
	TemplateRef,
	viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationSkipped, NavigationStart, Router } from '@angular/router';
import { SwipeUpDownDirective } from '@tools';
import { filter, fromEvent, tap } from 'rxjs';

import { UIIconComponent } from '@design-system';
import { BottomSheetService } from './bottom-sheet.service';

/**
 * App-wide bottom sheet for kala-mobile.
 *
 * Opens from the bottom with a dimmed backdrop. Closes on backdrop tap,
 * drag-down gesture, Escape, or browser back / route change.
 *
 * Multiple sheets are queued (FIFO): calling `open()` while another sheet is
 * already open waits until that sheet closes, then presents this one.
 *
 * @example
 * ```html
 * <app-bottom-sheet #sheet title="عنوان" (closed)="onClosed()">
 *   <ng-template #body>
 *     <p>محتوا</p>
 *   </ng-template>
 * </app-bottom-sheet>
 *
 * <button type="button" (click)="sheet.open()">باز کردن</button>
 * ```
 */
@Component({
	selector: 'app-bottom-sheet',
	imports: [NgTemplateOutlet, SwipeUpDownDirective, UIIconComponent],
	templateUrl: './bottom-sheet.component.html',
	styleUrl: './bottom-sheet.component.scss'
})
export class BottomSheetComponent implements OnDestroy {
	private readonly document = inject(DOCUMENT);
	private readonly renderer = inject(Renderer2);
	private readonly router = inject(Router);
	private readonly bottomSheetService = inject(BottomSheetService);
	private readonly host = inject(ElementRef<HTMLElement>);

	/** Optional title shown in the header when no `#header` template is projected. */
	readonly title = input<string>();
	/** When `true`, backdrop tap and drag-down dismiss the sheet. Use `'static'` to keep open. */
	readonly backdrop = input<boolean | 'static'>(true);
	/** Show the top drag handle (Figma handoff). */
	readonly showTopHandle = input(true);
	/** Extra classes on the body region. */
	readonly headerClass = input<string>('');
	readonly bodyClass = input<string>('');
	readonly footerClass = input<string>('');
	/**
	 * Keeps clicks inside the sheet from reaching `document`. Set it when the page behind
	 * has document-level click handling that must not fire while the sheet is up — e.g. the
	 * orders list snapping a swiped row shut while its delete confirmation is on screen.
	 * Off by default, so handlers inside a sheet (the custom keyboard) keep working.
	 */
	readonly blockOutsideClicks = input(false);
	/**
	 * Whether the sheet pushes a history entry so the hardware back button closes
	 * it instead of leaving the screen. On by default — that is what a sheet raised
	 * by a tap should do.
	 *
	 * Turn it off for a sheet raised from inside a `CanDeactivate` guard: a
	 * navigation is already in flight there, and pushing an entry the sheet would
	 * then pop on close leaves the router undoing a step the user never took.
	 */
	readonly trapBackNavigation = input(true);
	/** Accessible name when no title / labelledby is provided. */
	readonly ariaLabel = input<string>('');
	readonly ariaLabelledBy = input<string>('');
	readonly ariaDescribedBy = input<string>('');

	readonly closed = output<void>();

	readonly isOpen = signal(false);
	readonly showContent = signal(false);

	readonly bottomSheetHeader = contentChild<TemplateRef<unknown>>('header');
	readonly bottomSheetBody = contentChild<TemplateRef<unknown>>('body');
	readonly bottomSheetFooter = contentChild<TemplateRef<unknown>>('footer');

	private readonly overlayRef = viewChild<ElementRef<HTMLElement>>('overlay');
	private readonly bottomSheetRef = viewChild<ElementRef<HTMLElement>>('bottomSheet');

	private hasPushState = false;
	private isDestroyed = false;
	private portalNode: HTMLElement | null = null;
	private closeTimer: ReturnType<typeof setTimeout> | null = null;
	private closeResolve: (() => void) | null = null;
	private closePromise: Promise<void> | null = null;
	private pendingSelfPops = 0;
	private ignoredNavigationId: number | null = null;

	constructor() {
		toSignal(
			this.router.events.pipe(
				filter(
					(e): e is NavigationStart | NavigationSkipped =>
						e instanceof NavigationStart || e instanceof NavigationSkipped
				),
				tap(event => this.onNavigation(event))
			)
		);

		toSignal(
			fromEvent<KeyboardEvent>(this.document, 'keydown').pipe(
				filter(e => e.key === 'Escape' && this.isOpen() && this.backdrop() === true),
				tap(() => this.close())
			)
		);

		afterNextRender(() => {
			// If the sheet was opened before this callback ran, its host node is
			// already portaled into <body> — detaching it here would hide it.
			if (this.isOpen()) {
				return;
			}
			const node = this.host.nativeElement;
			this.portalNode = node;
			const parent = node.parentNode;
			if (parent) {
				this.renderer.removeChild(parent, node);
			}
		});
	}

	ngOnDestroy(): void {
		this.isDestroyed = true;
		this.settleClose();
		if (this.isOpen()) {
			this.teardownDom();
			this.removePushState();
			this.document.body.classList.remove('overflow-hidden');
		} else if (this.portalNode?.parentNode === this.document.body) {
			this.renderer.removeChild(this.document.body, this.portalNode);
		}
		this.bottomSheetService.dequeue(this);
	}

	/**
	 * Request to open this sheet. If another sheet is already open, this one
	 * waits in the service queue and opens after the current sheet closes.
	 */
	open(): Promise<void> {
		if (this.isOpen()) {
			return Promise.resolve();
		}
		// Re-opening while the close animation is still running: cancel the
		// pending close and present again instead of letting it finish closing.
		if (this.bottomSheetService.current() === this) {
			return this.present();
		}
		return this.bottomSheetService.enqueue(this);
	}

	/**
	 * Actually show the sheet in the DOM. Called by {@link BottomSheetService}
	 * when this instance becomes the active queue item — do not call from features.
	 */
	async present(): Promise<void> {
		if (this.isOpen() || this.isDestroyed) {
			return;
		}

		this.settleClose();

		const portalNode = this.portalNode ?? this.host.nativeElement;
		this.portalNode = portalNode;

		this.isOpen.set(true);
		this.showContent.set(true);

		if (portalNode.parentNode !== this.document.body) {
			this.renderer.appendChild(this.document.body, portalNode);
		}
		this.document.body.classList.add('overflow-hidden');

		await new Promise<void>(resolve => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (!this.isOpen()) {
						resolve();
						return;
					}
					this.bottomSheetRef()?.nativeElement.classList.add('is-open');
					this.overlayRef()?.nativeElement.classList.add('is-open');
					this.addPushState();
					resolve();
				});
			});
		});
	}

	close(): Promise<void> {
		if (!this.isOpen() && !this.showContent()) {
			return Promise.resolve();
		}
		if (this.closePromise) {
			return this.closePromise;
		}

		this.bottomSheetRef()?.nativeElement.classList.remove('is-open');
		this.overlayRef()?.nativeElement.classList.remove('is-open');
		this.isOpen.set(false);
		this.removePushState();
		this.document.body.classList.remove('overflow-hidden');

		this.closePromise = new Promise<void>(resolve => {
			this.closeResolve = resolve;
			this.closeTimer = setTimeout(() => {
				this.closeTimer = null;
				this.closeResolve = null;
				this.closePromise = null;
				this.showContent.set(false);
				this.teardownDom();
				this.bottomSheetService.dequeue(this);
				if (!this.isDestroyed) {
					this.closed.emit();
				}
				resolve();
			}, 500);
		});

		return this.closePromise;
	}

	onBackdropClick(event: MouseEvent): void {
		this.stopWhenBlocking(event);
		if (this.backdrop() === true) {
			this.close();
		}
	}

	onPanelClick(event: MouseEvent): void {
		this.stopWhenBlocking(event);
	}

	onSwipeHide(): void {
		if (this.backdrop() === true) {
			this.close();
		}
	}

	private stopWhenBlocking(event: MouseEvent): void {
		if (this.blockOutsideClicks()) {
			event.stopPropagation();
		}
	}

	private teardownDom(): void {
		if (this.portalNode?.parentNode === this.document.body) {
			this.renderer.removeChild(this.document.body, this.portalNode);
		}
	}

	/** Cancel an in-flight close animation and resolve its pending promise. */
	private settleClose(): void {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}
		this.closePromise = null;
		if (this.closeResolve) {
			const resolve = this.closeResolve;
			this.closeResolve = null;
			resolve();
		}
	}

	private onNavigation(event: NavigationStart | NavigationSkipped): void {
		if (event.id === this.ignoredNavigationId) {
			return;
		}
		if (this.pendingSelfPops > 0) {
			this.pendingSelfPops--;
			this.ignoredNavigationId = event.id;
			return;
		}
		if (!this.hasPushState || !this.isOpen()) {
			return;
		}
		this.removePushState(true);
		this.close();
	}

	private addPushState(): void {
		if (!this.trapBackNavigation()) {
			return;
		}
		if (!this.hasPushState) {
			this.hasPushState = true;
			history.pushState(null, '');
		}
	}

	private removePushState(withNavigation = false): void {
		if (!withNavigation && this.hasPushState) {
			this.pendingSelfPops++;
			history.go(-1);
		}
		this.hasPushState = false;
	}
}
